import styled from '@emotion/styled';
import {
  Option,
  Popper,
  Select,
  selectClasses,
  SelectRootSlotProps,
} from '@mui/base';
import React, {forwardRef} from 'react';

import {ChevronSmallDown, ChevronSmallUp} from '@ELib/assets/images';
import {
  mixinCenterWrapper,
  mixinSVGIcon,
  mixinTruncate,
} from '@ELib/styles/mixins';

export type ELabelSelectOption = {
  value: string;
  displayText: string;
};

export type ELabelSelectProps = {
  value?: string;
  name?: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: ELabelSelectOption[];
  ariaLabel?: string;
};

const RootElement = forwardRef(
  <TValue extends object, Multiple extends boolean>(
    props: SelectRootSlotProps<TValue, Multiple> &
      React.RefAttributes<HTMLButtonElement>,
    ref: React.ForwardedRef<HTMLButtonElement>
  ) => {
    const {ownerState, children, ...otherProps} = props;
    return (
      <button {...otherProps} ref={ref}>
        <span>{children}</span>
        {ownerState.open ? <ChevronSmallUp /> : <ChevronSmallDown />}
      </button>
    );
  }
);

export const ELabelSelect = ({
  value,
  name,
  onChange,
  options,
  ariaLabel,
}: ELabelSelectProps): React.ReactElement => {
  const compatibilityOnChange = (
    e: React.MouseEvent | React.KeyboardEvent | React.FocusEvent | null,
    newValue: string | null
  ) => {
    // if the event does not exist we do not want to call onChange
    // this happens e.g. if the options change and the value is not in the options anymore
    // this would lead to a onChange call with value null
    if (!e) {
      return;
    }
    onChange({
      type: 'change',
      target: {
        name,
        value: newValue || '',
      },
    } as React.ChangeEvent<HTMLSelectElement>);
  };

  return (
    <Select
      aria-label={ariaLabel}
      slots={{
        root: StyledRoot,
        listbox: StyledListbox,
        popper: StyledPopper,
      }}
      value={value}
      onChange={compatibilityOnChange}
    >
      {options.map((o) => (
        <Option key={o.value} value={o.value}>
          {o.displayText}
        </Option>
      ))}
    </Select>
  );
};

const StyledRoot = styled(RootElement)`
  font-size: var(--font-size-default);
  font-weight: var(--font-weight-thin);

  color: var(--color-core-primary);
  background: var(--color-core-white);

  border: none;

  padding: 0;
  margin-bottom: var(--gutter-tiny);
  ${mixinTruncate}
  ${mixinCenterWrapper}

  &.${selectClasses.focusVisible} {
    outline: var(--focus-border-width) solid var(--color-core-keyboardfocus);
  }

  &.${selectClasses.expanded} {
    outline: var(--focus-border-width) solid var(--color-core-keyboardfocus);
  }

  svg {
    ${mixinSVGIcon}

    color: var(--color-state-active);
    padding-left: var(--gutter-tiny);
  }
`;

const StyledPopper = styled(Popper)`
  z-index: var(--zindex-dropdown);
`;

const StyledListbox = styled.ul`
  overflow: auto;
  max-height: var(--dropdown-max-height);
  background: var(--color-core-white);
  border: var(--regular-border-width) solid var(--color-menu-border-focus);
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    color: var(--color-core-link);
    padding: var(--gutter-little) var(--gutter);
    font-weight: var(--dropdown-item-title);

    &.MuiOption-highlighted,
    &:hover {
      background-color: var(--color-state-hover);
      color: var(--color-core-black);
      cursor: pointer;
    }
  }
`;
